'use client';

import type React from 'react';

import axios from 'axios';
import { ChevronDown, Download, Shield } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import useWindowSize from 'src/adapters/primary/libs/useWindowSize';
import client from 'src/adapters/secondary/libs/client';
import { Button } from 'src/components/ui/button';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface CguModalProps {
  isOpen: boolean;
  onAccept: (cguId: number) => void;
}

export default function CguModal({ isOpen, onAccept }: CguModalProps) {
  const [cguPdf, setCguPdf] = useState(false);
  const [cgu, setCgu] = useState<{ url: string; id: any }>({ url: '', id: undefined });
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(null);
  const { width } = useWindowSize();
  const documentWidth = width ? (width < 640 ? width - 24 : 848) : 848;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const onDocumentLoadSuccess = ({ numPages }: { numPages: any }) => {
    setNumPages(numPages);
  };
  useEffect(() => {
    if (isOpen && !cguPdf) {
      getPDF();
    }
  }, []);
  useEffect(() => {
    getPDF();
  }, [isOpen, !cguPdf]);

  async function getPDF() {
    try {
      const { data: cgu } = await client.get(`/cgu/last-cgu`);
      const { data: pdf } = await axios(cgu.url, {
        method: 'GET',
        responseType: 'blob',
      });
      setCgu(cgu);
      setCguPdf(cgu.url !== '');
    } catch (e) {
      enqueueSnackbar(t('text.error') + '. ' + t('text.pleaseTryAgain'), {
        autoHideDuration: 7000,
        variant: 'error',
      });
    }
  }

  const downloadCgu = () => {
    if (cgu) {
      window.open(cgu.url, '_blank');
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // User has scrolled down
    if (scrollTop > 50) {
      setHasScrolled(true);
    }

    // Check if user is near the bottom (within 50px)
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setIsAtBottom(true);
    }
  };

  if (!isOpen) return null;
  console.log({ width });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative mx-4 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-gray-200 p-6">
          <div className="flex h-10 min-w-10 items-center justify-center rounded-lg bg-emerald-100">
            <Shield className="size-5 min-w-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('words.cgu')}</h2>
            <p className="text-sm text-gray-500">{t('text.readAllAndAcceptCgu')}</p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="h-0 w-full">
          {!hasScrolled && (
            <div className="flex w-full items-center justify-center">
              <div className="relative top-4 z-10 w-max animate-bounce rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-lg">
                <div className="flex items-center gap-2">
                  <span>{t('text.scrollToRead')}</span>
                  <ChevronDown className="h-4 w-4" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div
          className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex-1 overflow-y-auto p-6"
          onScroll={handleScroll}
          ref={scrollAreaRef}
        >
          {cguPdf && (
            <div className="relative min-h-[600px] overflow-x-hidden rounded-lg border border-gray-200 bg-white">
              <Document file={cgu.url} onLoadSuccess={onDocumentLoadSuccess}>
                {Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={documentWidth} // ajuste la largeur selon ton design
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ))}
              </Document>
            </div>
          )}
        </div>

        {/* Bottom gradient overlay when not at bottom */}
        {!isAtBottom && (
          <div className="pointer-events-none absolute bottom-20 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
        )}

        {/* Footer Actions */}
        <div className="flex flex-col items-center justify-between gap-4 rounded-b-2xl border-t border-gray-200 bg-gray-50 p-6 sm:flex-row">
          <Button
            dataId="download-pdf"
            variant="outline"
            className="flex items-center gap-2 bg-transparent"
            onClick={downloadCgu}
          >
            <Download className="h-4 w-4" />
            {t('text.downloadPDF')}
          </Button>

          <div className="flex flex-col items-center gap-4 sm:flex-row">
            {!isAtBottom && <p className="text-sm text-gray-500">{t('text.readAllDocument')}</p>}
            <Button
              dataId="accept-cgus"
              onClick={() => onAccept(cgu.id)}
              disabled={!isAtBottom}
              className="bg-emerald-600 px-8 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {isAtBottom ? t('button.accept') : t('button.readAllDocument')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
